/**
 * Safe areas (docs/RESPONSIVE.md). CSS reads `--sa-*` (tokens.css): the larger of env(safe-area-inset-*) and the insets
 * the Android shell injects as `--native-sa-*`. On a notched iPhone in landscape iOS reports the SAME inset on both sides
 * (≈ 47 pt), so a symmetric layout looks pushed in on the side without the notch. We mark the notch side from the
 * device orientation (window.orientation 90 = rotated counter-clockwise → the notch is on the left, -90 → right) and
 * tokens.css then keeps only a rounded-corner margin on the other side. Re-evaluated on every rotation. In the iOS app
 * the shell pushes the side from the interface orientation (window.__skNotch + an `sk-notch` event): a landscape-only
 * WKWebView reports window.orientation 0 until the phone is first rotated.
 */
const isIPhone = typeof navigator !== 'undefined' && /iPhone|iPod/.test(navigator.userAgent);

function orientationAngle(): number | null {
  const legacy = (window as unknown as { orientation?: number }).orientation;
  if (typeof legacy === 'number') return legacy;
  const t = screen.orientation?.type;
  if (t === 'landscape-primary') return 90;
  if (t === 'landscape-secondary') return -90;
  return null;
}

export function applyNotchSide(): void {
  const root = document.documentElement;
  // The iOS shell (SketchlingViewController) knows the interface orientation for sure and pushes window.__skNotch.
  const native = (window as unknown as { __skNotch?: string }).__skNotch;
  if (native === 'left' || native === 'right') {
    root.dataset.notch = native;
    return;
  }
  if (!isIPhone) return;
  const a = orientationAngle();
  if (a === 90) root.dataset.notch = 'left';
  else if (a === -90 || a === 270) root.dataset.notch = 'right';
  else delete root.dataset.notch;
}

/** `?debugSafeArea=1` (test flag): a corner badge with the insets, the orientation and the notch side. */
function debugBadge(): () => void {
  let el: HTMLDivElement | null = null;
  return () => {
    if (!el) {
      el = document.createElement('div');
      el.style.cssText =
        'position:fixed;left:50%;top:50%;transform:translate(-50%,-50%);z-index:9999;background:#000c;color:#fff;font:600 13px/1.4 monospace;padding:8px 12px;border-radius:8px;pointer-events:none;white-space:pre';
      document.body.appendChild(el);
    }
    const p = document.createElement('div');
    p.style.cssText = 'position:fixed;padding:var(--sa-t) var(--sa-r) var(--sa-b) var(--sa-l);visibility:hidden';
    const e = document.createElement('div');
    e.style.cssText = 'position:fixed;padding:env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left);visibility:hidden';
    document.body.append(p, e);
    const px = (x: HTMLElement) => ['Top', 'Right', 'Bottom', 'Left'].map((k) => parseFloat(getComputedStyle(x).getPropertyValue(`padding-${k.toLowerCase()}`))).join(' ');
    el.textContent = `env ${px(e)}\nsa  ${px(p)}\norientation ${String((window as unknown as { orientation?: number }).orientation)} ${screen.orientation?.type ?? '-'}\nnotch ${document.documentElement.dataset.notch ?? '-'}  ${innerWidth}x${innerHeight}`;
    p.remove();
    e.remove();
  };
}

export function initSafeArea(): void {
  if (typeof window === 'undefined') return;
  let debug: (() => void) | null = null;
  try {
    if (new URLSearchParams(location.search).get('debugSafeArea') === '1') sessionStorage.setItem('sk-debug-sa', '1');
    if (sessionStorage.getItem('sk-debug-sa') === '1') debug = debugBadge();
  } catch {
    /* no storage */
  }
  applyNotchSide();
  if (debug) {
    const d = debug;
    window.addEventListener('resize', () => setTimeout(d, 400));
    setInterval(d, 1500);
  }
  const again = () => {
    applyNotchSide();
    // iOS fires orientationchange before the new insets settle: check once more after the rotation animation.
    window.setTimeout(applyNotchSide, 350);
  };
  window.addEventListener('orientationchange', again);
  screen.orientation?.addEventListener?.('change', again);
  // A landscape-only app can start before WKWebView knows its interface orientation (window.orientation 0) and then
  // never get an orientationchange (it was already in that landscape side): look again once it has laid out, and on
  // every resize / return to the app.
  window.addEventListener('resize', applyNotchSide);
  window.addEventListener('sk-notch', applyNotchSide);
  document.addEventListener('visibilitychange', () => document.visibilityState === 'visible' && again());
  for (const ms of [300, 1000, 3000]) window.setTimeout(applyNotchSide, ms);
  pinViewport();
}

/**
 * The app is a fixed full-screen shell: the page itself never scrolls (inner areas do). iOS still scrolls the window
 * to show a focused field above the keyboard and does not always scroll it back when the keyboard closes, which left
 * the whole layout shifted (sign-in: left column cut off, back button gone). Put it back once nothing is focused.
 */
function pinViewport(): void {
  const reset = () => {
    const a = document.activeElement;
    if (a && /^(INPUT|TEXTAREA|SELECT)$/.test(a.tagName)) return;
    if (window.scrollX || window.scrollY) window.scrollTo(0, 0);
  };
  document.addEventListener('focusout', () => window.setTimeout(reset, 60));
  window.visualViewport?.addEventListener('resize', () => window.setTimeout(reset, 120));
  window.addEventListener('orientationchange', () => window.setTimeout(reset, 400));
}
